import { bopApi } from '@/lib/api/client';

export const NOTIFICATIONS_PATH = 'v1/notifications';

// Limits mirror bop-api src/core/notifications/content.js - the server rejects beyond these,
// and the caps are where mobile OSes truncate a push notification
export const TITLE_MAX_LENGTH = 65;
export const BODY_MAX_LENGTH = 240;
export const LINK_MAX_LENGTH = 200;

export const MESSAGE_STATUSES = ['DRAFT', 'IN_REVIEW', 'SENT', 'CANCELLED'] as const;
export type NotificationMessageStatus = (typeof MESSAGE_STATUSES)[number];

// Server-driven action set - the UI renders exactly these, never computes workflow rules itself
export const MESSAGE_ACTIONS = ['AMEND', 'SUBMIT', 'LEGAL_APPROVE', 'LEGAL_WITHDRAW', 'RETURN', 'CANCEL', 'SEND', 'SEND_REQUIRES_ACKNOWLEDGEMENT'] as const;
export type NotificationMessageAction = (typeof MESSAGE_ACTIONS)[number];

/** Raw message from /v1/notifications/messages* - every write returns the full updated record */
export interface RawNotificationMessage {
  message_id: string;
  status: string;
  title: string;
  body: string;
  data?: { link?: string };
  created_by: string;
  /** ISO-8601 */
  created_at: string;
  updated_at: string;
  updated_by?: string;
  approver_email?: string;
  submitted_at?: string;
  legal_approved_by?: string;
  legal_approved_at?: string;
  legal_comments?: string;
  review_comments?: string;
  sent_by?: string;
  sent_at?: string;
  sent_without_legal_review?: boolean;
  allowed_actions?: string[];
}

export interface NotificationMessage {
  messageId: string;
  status: NotificationMessageStatus;
  title: string;
  body: string;
  link: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  approverEmail: string | null;
  legalApprovedBy: string | null;
  legalApprovedAt: string | null;
  legalComments: string | null;
  reviewComments: string | null;
  sentBy: string | null;
  sentAt: string | null;
  sentWithoutLegalReview: boolean;
  allowedActions: NotificationMessageAction[];
}

export interface RawNotificationMessagesResponse {
  messages: RawNotificationMessage[];
  cursor?: string;
}

export interface NotificationMessagesPage {
  messages: NotificationMessage[];
  cursor?: string;
}

export interface RawNotificationsMeta {
  capabilities?: { canCompose?: boolean; canApprove?: boolean; canLegalReview?: boolean };
  approvers?: string[];
}

export interface NotificationsMeta {
  canCompose: boolean;
  canApprove: boolean;
  canLegalReview: boolean;
  approvers: string[];
}

// A newer bop-api may ship statuses this build doesn't know - degrade to CANCELLED (inert,
// terminal display) rather than crashing the list; allowed_actions still drives the buttons
const toMessageStatus = (value: string): NotificationMessageStatus =>
  MESSAGE_STATUSES.includes(value as NotificationMessageStatus) ? (value as NotificationMessageStatus) : 'CANCELLED';

// Unknown actions are dropped - a stale UI must never offer an action it cannot express
const knownActions = (values: string[] | undefined): NotificationMessageAction[] =>
  (values ?? []).filter((value): value is NotificationMessageAction => MESSAGE_ACTIONS.includes(value as NotificationMessageAction));

export const mapNotificationMessage = (raw: RawNotificationMessage): NotificationMessage => ({
  messageId: raw.message_id,
  status: toMessageStatus(raw.status),
  title: raw.title,
  body: raw.body,
  link: raw.data?.link ?? null,
  createdBy: raw.created_by,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at,
  approverEmail: raw.approver_email ?? null,
  legalApprovedBy: raw.legal_approved_by ?? null,
  legalApprovedAt: raw.legal_approved_at ?? null,
  legalComments: raw.legal_comments ?? null,
  reviewComments: raw.review_comments ?? null,
  sentBy: raw.sent_by ?? null,
  sentAt: raw.sent_at ?? null,
  sentWithoutLegalReview: raw.sent_without_legal_review ?? false,
  allowedActions: knownActions(raw.allowed_actions),
});

export const mapNotificationMessagesResponse = (raw: RawNotificationMessagesResponse): NotificationMessagesPage => ({
  messages: (raw.messages ?? []).map(mapNotificationMessage),
  cursor: raw.cursor,
});

// Capabilities default FALSE (read-only fail-safe)
export const mapNotificationsMeta = (raw: RawNotificationsMeta): NotificationsMeta => ({
  canCompose: raw.capabilities?.canCompose ?? false,
  canApprove: raw.capabilities?.canApprove ?? false,
  canLegalReview: raw.capabilities?.canLegalReview ?? false,
  approvers: raw.approvers ?? [],
});

export interface NotificationMessageContentRequest {
  title: string;
  body: string;
  data?: { link: string };
}

export type ReviewMessageRequest =
  | { action: 'SUBMIT'; approver_email: string }
  | { action: 'LEGAL_APPROVE' | 'LEGAL_WITHDRAW'; comment?: string }
  | { action: 'RETURN'; comment: string }
  | { action: 'CANCEL' };

// Post-write cache patchers: every write returns the full message (with the caller's
// allowed_actions), so the client patches list pages from the response instead of refetching

export const patchRawListRowMessage = (raw: RawNotificationMessagesResponse | undefined, message: RawNotificationMessage): RawNotificationMessagesResponse | undefined =>
  raw ? { ...raw, messages: (raw.messages ?? []).map((row) => (row.message_id === message.message_id ? message : row)) } : raw;

export const prependRawListRowMessage = (raw: RawNotificationMessagesResponse | undefined, message: RawNotificationMessage): RawNotificationMessagesResponse | undefined =>
  raw ? { ...raw, messages: [message, ...(raw.messages ?? [])] } : raw;

export const getNotificationsMeta = () => bopApi.get(`${NOTIFICATIONS_PATH}/meta`).json<RawNotificationsMeta>();

export const getNotificationMessages = (cursor: string | undefined, signal?: AbortSignal) =>
  bopApi.get(`${NOTIFICATIONS_PATH}/messages`, { ...(cursor ? { searchParams: { cursor } } : {}), signal }).json<RawNotificationMessagesResponse>();

// Write errors carry enum codes in { error: string }: 409 CONFLICT (record changed underneath
// you - refresh and retry), 422 ILLEGAL_TRANSITION and the send guards
// (SELF_APPROVAL_REQUIRES_LEGAL, LEGAL_REVIEW_NOT_ACKNOWLEDGED), 403 permission denials,
// 400 INVALID_APPROVER - map codes to copy in the UI layer

export const createNotificationMessage = (body: NotificationMessageContentRequest) => bopApi.post(`${NOTIFICATIONS_PATH}/messages`, { json: body }).json<RawNotificationMessage>();

export const updateNotificationMessage = (messageId: string, body: NotificationMessageContentRequest) =>
  bopApi.put(`${NOTIFICATIONS_PATH}/messages/${messageId}`, { json: body }).json<RawNotificationMessage>();

export const reviewNotificationMessage = (messageId: string, body: ReviewMessageRequest) =>
  bopApi.post(`${NOTIFICATIONS_PATH}/messages/${messageId}/review`, { json: body }).json<RawNotificationMessage>();

export const sendNotificationMessage = (messageId: string, acknowledgeNoLegalReview: boolean) =>
  bopApi.post(`${NOTIFICATIONS_PATH}/messages/${messageId}/send`, { json: { acknowledge_no_legal_review: acknowledgeNoLegalReview } }).json<RawNotificationMessage>();
